import axios from 'axios'
import { v4 as uuidv4 } from 'uuid'
import { useEffect } from 'react'
import { GoogleGenerativeAI } from '@google/generative-ai'
import { HarmBlockThreshold, HarmCategory } from '@google/generative-ai'
import { polysemicKeywords } from './polysemicKeywords'
import { NoUsedTags } from 'constants'

import { pictograms } from './pictograms'

export const generatePictogramData = async (jsonData) => {
  // List of functions that modify the jsonData synchronously
  const syncFixerFunctions = [
    transformArray,
    addFields,
    generateUUIDs,
    getPictogramData,
    processPolysemicItems,
    sortByScore,
  ]

  const dataProcessed = await syncFixerFunctions.reduce(
    async (accPromise, func) => {
      const acc = await accPromise // Wait for the previous result
      return func(acc) // Apply the current function
    },
    Promise.resolve(jsonData) // Start with a resolved Promise containing the initial data
  )

  // Usage

  // Initialize an object to store the pictograms by their UUID
  const objPictograms = {}

  // Populate the objPictograms object with the UUIDs and corresponding pictograms
  dataProcessed.forEach((pictogram) => {
    objPictograms[pictogram.uuid] = pictogram

    // Fix case where a parent has no pictos
    if (!pictogram.pictos.length && pictogram.parent) {
      objPictograms[pictogram.parent].children = []
      objPictograms[pictogram.parent].debug += ' fixNoChildrenWithPictos'
    }
  })

  return objPictograms
}

function transformArray(inputArray) {
  return inputArray.flatMap((entry) =>
    entry.items.map((item) => ({ source: item, category: entry.category }))
  )
}

// use in PictogramCard!!!
export const isAuxiliary = ({ wordType, outcome }) =>
  wordType === 'auxiliar verb' || wordType === 'auxiliar'

function addFields(arr) {
  return arr.map((obj) => {
    return {
      ...obj,
      debug: '',
      children: [],
      parent: '',
      imageUrl: '',
      currentIndex: 0,
      visible: true,
    }
  })
}

function generateUUIDs(arr) {
  return arr.map((obj) => {
    return { ...obj, uuid: uuidv4() }
  })
}

function getPictogramData(jsonData) {
  return jsonData.map((item) => {
    const keyword = getKeyword(item)
    if (!keyword) return { ...item, pictos: [] }
    else return { ...item, pictos: pictograms[keyword] || [] }
  })
}

/* returns source or outcome in toLowerCase*/
const getKeyword = (pictogram) => {
  const { outcome: out, source: src, debug, wordType } = pictogram
  const outcome = out?.toLowerCase()
  const source = src?.toLowerCase()
  // Check if keyword is valid
  if (!source && !outcome) return null
  if (isAuxiliary(pictogram)) return null
  if (debug.includes('joinLongWordsByField')) return outcome
  if (wordType === 'verb' && polysemicVerbs.includes(source)) return outcome
  // verb should use source because maybe is a word like "estropeado" but usually it will take outcome
  return pictograms[source] ? source : pictograms[outcome] ? outcome : null
}

const polysemicVerbs = [
  'tumba',
  'tumbas',
  'cojo',
  'barro',
  'vino',
  'corte',
  'cortes',
  'nada',
  'paso',
  'llama',
  'llamas',
  'abrigo',
  'abrigos',
  'como',
  'cambio',
  'cuenta',
  'cuentas',
  'cuento',
  'dibujo',
  'duda',
  'dudas',
  'falta',
  'faltas',
  'firma',
  'juego',
  'puesto',
  'corta',
  'cortas',
]

export const safetySettings = [
  {
    category: HarmCategory.HARM_CATEGORY_HARASSMENT,
    threshold: HarmBlockThreshold.BLOCK_NONE,
  },
  {
    category: HarmCategory.HARM_CATEGORY_HATE_SPEECH,
    threshold: HarmBlockThreshold.BLOCK_NONE,
  },
  {
    category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT,
    threshold: HarmBlockThreshold.BLOCK_NONE,
  },
  {
    category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT,
    threshold: HarmBlockThreshold.BLOCK_NONE,
  },
]

// TODO: refactor somehow the api requests.... taking into account if
// item is already fetched via jotai
export const useFetchData = (urls, setState, dependencies = []) => {
  useEffect(() => {
    const fetchData = async () => {
      try {
        // If it's an array of URLs, we use Promise.all to fetch them concurrently
        const responses = await Promise.all(urls.map((url) => axios.get(url)))

        // Extract data from the responses
        const data = responses.map((response) => response.data)

        // Update state with fetched data
        setState(data)
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }

    fetchData()
    // `dependencies` allows you to conditionally refetch data if needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies)
}

// const genAI = new GoogleGenerativeAI(process.env.REACT_APP_API_KEY)
const genAI = new GoogleGenerativeAI('AIzaSyDaRcnf0ax6mNxMO2NtL4_DTG0YNw2mMmc')

export const model = genAI.getGenerativeModel({
  model: 'gemini-1.5-flash-8b',
  safetySettings,
})

export const boardModel = genAI.getGenerativeModel({
  // model: 'tunedModels/ia-jm--64-ordenes--30e-60b-77k66rv03oo3',
  model: 'gemini-1.5-flash-8b',
  safetySettings,
})

function processPolysemicItems(items) {
  // Define tags to ignore

  // Step 1: Filter out polysemic items
  const polysemicItems = items.filter(
    ({ source, outcome }) =>
      polysemicKeywords.includes(outcome) || polysemicKeywords.includes(source)
  )

  // If no polysemic items are found, return early
  if (!polysemicItems.length) return items

  // Debug array for all polysemic calculations
  const debugResults = []

  // Step 2: For each polysemic item, calculate scores for its pictos
  polysemicItems.forEach((polyItem, polyItemIndex) => {
    // if (polyItem.wordType === 'verb') return
    const polysemicDebug = { source: polyItem.source, pictos: [] }

    polyItem.pictos.forEach((polyPicto, pictoIndex) => {
      const pictoDebug = {
        id: polyPicto.id,
        tags: polyPicto.tags.map((tag) =>
          NoUsedTags.includes(tag) ? `(${tag})` : tag
        ),
        comparisons: [],
        finalScore: 0, // Initialize finalScore for this picto
      }

      // Initialize score for the picto
      let score = 0

      // Step 3: Compare this picto with all other pictos in the sentence
      items.forEach((item, itemIndex) => {
        // Exclude the same item (no self-comparison)
        if (item === polyItem) return
        // no wordType here, yes in araword but not here
        // if (!['noun', 'adjective', 'verb'].includes(item.wordType)) return

        item.pictos.forEach((picto) => {
          const filteredPolyTags = polyPicto.tags.filter(
            (tag) => !NoUsedTags.includes(tag)
          )
          const filteredOtherTags = picto.tags.filter(
            (tag) => !NoUsedTags.includes(tag)
          )

          // Skip comparison if either set of tags is empty
          if (!filteredPolyTags.length || !filteredOtherTags.length) return

          const comparisonDebug = {
            comparedTo: {
              id: picto.id,
              tags: picto.tags.map((tag) =>
                NoUsedTags.includes(tag) ? `(${tag})` : tag
              ),
              source: item.source,
            },
            intersection: 0,
            weightedScore: 0,
            normalizationFactor: 0,
            finalScoreContribution: 0,
          }

          // Calculate the intersection of the tags
          const intersection = filteredPolyTags.filter((tag) =>
            filteredOtherTags.includes(tag)
          ).length
          comparisonDebug.intersection = intersection

          // Calculate weighted score based on tag sizes
          const weightedScore =
            (intersection * 2) /
            (filteredPolyTags.length + filteredOtherTags.length)
          comparisonDebug.weightedScore = weightedScore

          // Normalize by the number of pictos in the other item
          const normalizationFactor = 1 / item.pictos.length
          comparisonDebug.normalizationFactor = normalizationFactor

          // Update score based on weightedScore, proximity, and normalization factor
          const finalScoreContribution = weightedScore * normalizationFactor
          comparisonDebug.finalScoreContribution = finalScoreContribution

          // Accumulate score
          score += finalScoreContribution

          // Add comparison details to debug
          pictoDebug.comparisons.push(comparisonDebug)
        })
      })

      // Assign the computed score to the polyPicto
      polyPicto.score = score

      // Save the final score in the debug object
      pictoDebug.finalScore = score

      // Add debug info for this picto
      polysemicDebug.pictos.push(pictoDebug)
    })

    // Sort the pictos array in debug by finalScore in descending order
    polysemicDebug.pictos.sort((a, b) => b.finalScore - a.finalScore)

    // Add the debug info for this polysemic item to the overall debug array
    debugResults.push(polysemicDebug)
  })
  console.log(`polysemic:`, debugResults)
  // Step 4: Return the updated items with scores assigned to polysemic pictos
  return items
}

function sortByScore(items) {
  // Sort the items by the score of their pictos
  return items.map((item) => {
    // Sort the pictos by score in descending order
    item.pictos.sort((a, b) => b?.score - a?.score)
    return item
  })
}
